import { useEffect } from "react";
import RecorderControls from "./recorder-controls";
import { formatTime } from "./utils/format-timer";
import RecorderVisualizer from "./recorder-visualizer";
import RecorderAudioPlayer from "./recorder-audio-player";
import ConfirmDialog from "../ConfirmDialog";
import MicrophoneIcon from "@/assets/icons/mic.svg?react";
import { t } from "i18next";

const RecorderUIInner = ({
  status,
  startRecording,
  stopRecording,
  pauseRecording,
  resumeRecording,
  mediaBlobUrl,
  mediaStream,
  autoStarted,
  setAutoStarted,
  recordingTime,
  setRecordingTime,
  showConfirmation,
  setShowConfirmation,
  finished,
  setFinished,
}: {
  status: string;
  startRecording: () => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  mediaBlobUrl: string | null;
  mediaStream: MediaStream | null;
  autoStarted: boolean;
  setAutoStarted: (value: boolean) => void;
  recordingTime: number;
  setRecordingTime: (value: number | ((previous: number) => number)) => void;
  showConfirmation: boolean;
  setShowConfirmation: (value: boolean) => void;
  finished: boolean;
  setFinished: (value: boolean) => void;
}): JSX.Element => {
  useEffect(() => {
    if (!autoStarted) {
      startRecording();
      setAutoStarted(true);
    }
  }, [autoStarted, startRecording, setAutoStarted]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (status === "recording") {
      timer = setInterval(() => {
        setRecordingTime((previous) => previous + 1);
      }, 1000);
    }
    return (): void => {
      if (timer) clearInterval(timer);
    };
  }, [status, setRecordingTime]);

  const handleConfirmYes = (): void => {
    stopRecording();
    setFinished(true);
    setShowConfirmation(false);
  };

  if (!finished) {
    return (
      <div className="w-full gap-4 justify-center items-center flex flex-col relative">
        <MicrophoneIcon
          aria-label="Mic"
          height={100}
          strokeWidth="0.5"
          width={100}
        />
        <RecorderVisualizer audio={mediaStream} paused={status === "paused"} />
        <p className="font-medium">{formatTime(recordingTime)}</p>
        <ConfirmDialog
          description={t("components.recorderInner.finishAudioDescription")}
          open={showConfirmation}
          title={t("components.recorderInner.finishAudioTitle")}
          onConfirm={handleConfirmYes}
          onOpenChange={setShowConfirmation}
        />
        {!showConfirmation && (
          <RecorderControls
            status={status}
            onPause={pauseRecording}
            onResume={resumeRecording}
            onStopAndSave={() => {
              pauseRecording();
              setShowConfirmation(true);
            }}
          />
        )}
      </div>
    );
  } else {
    return (
      <>{mediaBlobUrl && <RecorderAudioPlayer mediaBlobUrl={mediaBlobUrl} />}</>
    );
  }
};

export default RecorderUIInner;
